import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Save, ChevronDown, ChevronUp, Plus, X, Trash2, Star } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { clearTeamLeadReportCache } from '../timesheet/TeamLeadReportChip';

const API = process.env.REACT_APP_BACKEND_URL;

// Canonical services the team lead can log against. Kept intentionally
// short — user-locked decision (2026-07-24) to reduce mobile friction.
// Adding a new service is a two-place edit: this list + the metric
// config below.
const CANONICAL_SERVICES = [
  'Soft Dig',
  'CCTV Inspection',
  'Flush/Jetting',
];

// Per-service structured-metric config. Each service can have exactly
// one primary numeric counter (holes dug, lines inspected, lines
// flushed, etc.) rendered above the notes textarea. `key` is the field
// name on the backend ServiceEntry model; adding a new metric means:
//   1. Add an Optional[int] field with the same key on the backend
//      routes/daily_reports.py::ServiceEntry model
//   2. Add an entry here
//   3. Update the display in components/timesheet/TeamLeadReportChip.js
const SERVICE_METRICS = {
  'Soft Dig':        { key: 'holes_dug',       label: 'Holes dug today',       placeholder: 'e.g. 11' },
  'CCTV Inspection': { key: 'lines_inspected', label: 'Lines Inspected Today', placeholder: 'e.g. 5'  },
  'Flush/Jetting':   { key: 'lines_flushed',   label: 'Lines Flushed Today',   placeholder: 'e.g. 5'  },
};

// Case-insensitive dedup that preserves the first-seen casing.
function mergeServices(...lists) {
  const out = [];
  const seen = new Set();
  for (const list of lists) {
    for (const s of list || []) {
      const key = (s || '').trim().toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(s);
    }
  }
  return out;
}

/**
 * Team Lead Daily Report — mobile panel rendered at the bottom of the
 * Field Time Tracker. Only appears if the current user is the Team Lead
 * on at least one job that overlaps with the tracker's selected date.
 *
 * Behaviour:
 *  - Fetches jobs the user is team lead on for the given date.
 *  - For each such job, renders a card with a checkbox row of the job's
 *    service types (pulled from proposal line_items via
 *    /api/daily-reports/services-for-job/{job_id}).
 *  - Toggling a service checkbox opens an inline form for that service:
 *    notes (all), holes_dug (Soft Dig only), equipment_used (multi-select
 *    against /api/assets).
 *  - Saves via PUT /api/daily-reports (upserts by job_id + date).
 *
 * Props:
 *   userId   — the current user's id
 *   date     — YYYY-MM-DD, the day the tracker is showing
 *   token    — auth token
 */
export default function DailyReportPanel({ userId, date, token }) {
  const [loading, setLoading] = useState(true);
  const [leadJobs, setLeadJobs] = useState([]);   // jobs where the user is team lead for `date`
  const [reports, setReports] = useState({});      // { job_id: report_doc }
  const [serviceLists, setServiceLists] = useState({}); // { job_id: [service_names] } (line_items only)
  const [equipmentOptions, setEquipmentOptions] = useState([]);
  const [recentEquipment, setRecentEquipment] = useState([]);  // top N recently-used across reports
  const [expandedJob, setExpandedJob] = useState(null);
  const [saving, setSaving] = useState({});        // { job_id: boolean }
  const [deleting, setDeleting] = useState({});    // { job_id: boolean }
  const [pendingDelete, setPendingDelete] = useState(null); // { jobId, reportId, jobTitle }

  const authHeaders = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  // Load jobs where the current user is team lead for the given date.
  // Also load equipment (assets) list for the multi-select, plus the
  // lead's recent-equipment usage (last 21 days) for a "Recent" quick-add
  // row so they don't have to search-and-tap the same items every day.
  const loadEverything = useCallback(async () => {
    if (!userId || !date || !token) return;
    setLoading(true);
    try {
      // Recent-equipment window: 21 days back from the tracker date.
      const dt = new Date(date);
      dt.setDate(dt.getDate() - 21);
      const dateFrom = dt.toISOString().slice(0, 10);

      const [jobsResp, assetsResp, historyResp] = await Promise.all([
        axios.get(`${API}/api/jobs`, {
          ...authHeaders,
          params: { date, assigned_to: userId, limit: 100 },
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/assets`, authHeaders).catch(() => ({ data: [] })),
        axios.get(`${API}/api/daily-reports`, {
          ...authHeaders,
          params: { team_lead_id: userId, date_from: dateFrom, date_to: date },
        }).catch(() => ({ data: [] })),
      ]);
      const allJobs = Array.isArray(jobsResp.data)
        ? jobsResp.data
        : (jobsResp.data?.jobs || []);
      const myLeadJobs = allJobs.filter(j => j.team_lead_id === userId);

      const assets = Array.isArray(assetsResp.data) ? assetsResp.data : [];
      setEquipmentOptions(
        assets.map(a => ({
          id: a.id,
          name: a.name || a.asset_number,
          category: a.category,
          code: a.asset_number,
        }))
      );

      // Frequency-count equipment across recent reports; keep top 6.
      // Only include items still present in the current assets list so
      // we don't surface deleted equipment.
      const validIds = new Set(assets.map(a => a.id));
      const freq = new Map();  // id -> { count, ref }
      for (const rpt of (historyResp.data || [])) {
        for (const se of (rpt.service_entries || [])) {
          for (const eq of (se.equipment_used || [])) {
            if (!validIds.has(eq.id)) continue;
            const cur = freq.get(eq.id) || { count: 0, ref: eq };
            cur.count += 1;
            freq.set(eq.id, cur);
          }
        }
      }
      setRecentEquipment(
        [...freq.values()]
          .sort((a, b) => b.count - a.count)
          .slice(0, 6)
          .map(x => x.ref)
      );

      setLeadJobs(myLeadJobs);

      // For each lead-job, load its proposal-services list + existing report.
      const serviceMap = {};
      const reportMap = {};
      await Promise.all(myLeadJobs.map(async (job) => {
        try {
          const [svcResp, reportResp] = await Promise.all([
            axios.get(`${API}/api/daily-reports/services-for-job/${job.id}`, authHeaders),
            axios.get(`${API}/api/daily-reports`, {
              ...authHeaders,
              params: { job_id: job.id, date },
            }),
          ]);
          serviceMap[job.id] = svcResp.data?.services || [];
          const existing = (reportResp.data || [])[0];
          if (existing) reportMap[job.id] = existing;
        } catch (err) {
          console.warn('daily-report load failed for job', job.id, err?.message);
        }
      }));
      setServiceLists(serviceMap);
      setReports(reportMap);
    } finally {
      setLoading(false);
    }
  }, [userId, date, token, authHeaders]);

  useEffect(() => { loadEverything(); }, [loadEverything]);

  // Ensure the report state has a service_entries row for `service` (creating
  // one if the lead just toggled the checkbox on).
  const ensureEntry = (jobId, service) => {
    setReports(prev => {
      const cur = prev[jobId] || { job_id: jobId, date, service_entries: [] };
      const has = cur.service_entries.some(e => e.service === service);
      if (has) return prev;
      return {
        ...prev,
        [jobId]: {
          ...cur,
          service_entries: [
            ...cur.service_entries,
            {
              service,
              notes: '',
              holes_dug: null,
              lines_inspected: null,
              lines_flushed: null,
              equipment_used: [],
            },
          ],
        },
      };
    });
  };

  const removeEntry = (jobId, service) => {
    setReports(prev => {
      const cur = prev[jobId];
      if (!cur) return prev;
      return {
        ...prev,
        [jobId]: {
          ...cur,
          service_entries: cur.service_entries.filter(e => e.service !== service),
        },
      };
    });
  };

  const updateEntry = (jobId, service, patch) => {
    setReports(prev => {
      const cur = prev[jobId];
      if (!cur) return prev;
      return {
        ...prev,
        [jobId]: {
          ...cur,
          service_entries: cur.service_entries.map(e =>
            e.service === service ? { ...e, ...patch } : e
          ),
        },
      };
    });
  };

  const toggleEquipment = (jobId, service, item) => {
    setReports(prev => {
      const cur = prev[jobId];
      if (!cur) return prev;
      return {
        ...prev,
        [jobId]: {
          ...cur,
          service_entries: cur.service_entries.map(e => {
            if (e.service !== service) return e;
            const has = (e.equipment_used || []).some(eq => eq.id === item.id);
            const next = has
              ? e.equipment_used.filter(eq => eq.id !== item.id)
              : [...(e.equipment_used || []), item];
            return { ...e, equipment_used: next };
          }),
        },
      };
    });
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const { jobId, reportId } = pendingDelete;
    setDeleting(s => ({ ...s, [jobId]: true }));
    try {
      await axios.delete(`${API}/api/daily-reports/${reportId}`, authHeaders);
      // Drop the local report and collapse the card so it looks like a
      // fresh slate. The card stays visible (this user is still Team
      // Lead of the job) but with no logged services.
      setReports(prev => {
        const next = { ...prev };
        delete next[jobId];
        return next;
      });
      clearTeamLeadReportCache();
      toast.success('Team Lead Report deleted');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Failed to delete report');
    } finally {
      setDeleting(s => ({ ...s, [jobId]: false }));
      setPendingDelete(null);
    }
  };

  const saveReport = async (jobId) => {
    const cur = reports[jobId];
    if (!cur) return;
    setSaving(s => ({ ...s, [jobId]: true }));
    try {
      const payload = {
        job_id: jobId,
        date,
        service_entries: (cur.service_entries || []).map(e => {
          const metric = SERVICE_METRICS[e.service];
          const asInt = (v) => (v === '' || v == null ? null : Number(v));
          return {
            service: e.service,
            notes: e.notes || '',
            // Only send the numeric counter that belongs to this
            // service; leave the others null so a Flush/Jetting entry
            // never accidentally saves a hole count.
            holes_dug:       metric?.key === 'holes_dug'       ? asInt(e.holes_dug)       : null,
            lines_inspected: metric?.key === 'lines_inspected' ? asInt(e.lines_inspected) : null,
            lines_flushed:   metric?.key === 'lines_flushed'   ? asInt(e.lines_flushed)   : null,
            equipment_used: (e.equipment_used || []).map(eq => ({
              id: eq.id, name: eq.name, category: eq.category || null,
            })),
          };
        }),
      };
      const resp = await axios.put(`${API}/api/daily-reports`, payload, authHeaders);
      setReports(prev => ({ ...prev, [jobId]: resp.data }));
      // Invalidate the Timesheet chip's shared cache so the office sees
      // the update immediately without a page reload.
      clearTeamLeadReportCache();
      // Auto-collapse the card after a successful save — the summary
      // line ("N services logged — tap to expand") gives the lead
      // confirmation without them having to close it manually.
      setExpandedJob(prev => (prev === jobId ? null : prev));
      toast.success('Daily report saved');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Failed to save report');
    } finally {
      setSaving(s => ({ ...s, [jobId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="px-4 mt-4">
        <Card>
          <CardContent className="flex items-center gap-2 py-4 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" />
            Checking Team Lead assignments…
          </CardContent>
        </Card>
      </div>
    );
  }

  // Hidden entirely for non-leads (the visibility contract we agreed on).
  if (leadJobs.length === 0) return null;

  return (
    <div className="px-4 mt-4 space-y-4" data-testid="daily-report-panel">
      {leadJobs.map(job => {
        // Checkbox list = the 3 canonical services PLUS any services
        // already saved on this report (defensive — preserves visibility
        // of legacy entries logged before the list was trimmed).
        // Job line_items are intentionally NOT auto-included per user
        // decision 2026-07-24: keep the mobile picker to 3 items.
        const report = reports[job.id] || { service_entries: [] };
        const reportServiceNames = report.service_entries.map(e => e.service);
        const services = mergeServices(reportServiceNames, CANONICAL_SERVICES);
        const activeServices = new Set(reportServiceNames);
        const isExpanded = expandedJob === job.id;
        return (
          <Card key={job.id} className="border-amber-200" data-testid={`daily-report-card-${job.id}`}>
            <CardHeader
              className="pb-2 cursor-pointer"
              onClick={() => setExpandedJob(isExpanded ? null : job.id)}
            >
              <CardTitle className="flex items-center justify-between text-base gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Star className="w-5 h-5 fill-amber-500 text-amber-500 shrink-0" />
                  <span className="font-['Barlow_Condensed'] truncate">Team Lead Report</span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {report.id && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPendingDelete({
                          jobId: job.id,
                          reportId: report.id,
                          jobTitle: job.job_title || job.title || 'this job',
                        });
                      }}
                      title="Delete this Team Lead Report"
                      data-testid={`daily-report-delete-${job.id}`}
                      disabled={!!deleting[job.id]}
                      className="p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50"
                      aria-label="Delete this Team Lead Report"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  {isExpanded
                    ? <ChevronUp className="w-4 h-4" />
                    : <ChevronDown className="w-4 h-4" />}
                </div>
              </CardTitle>
              <div className="text-xs text-slate-600 truncate">{job.job_title || job.title}</div>
              <div className="text-xs text-slate-400">{date}</div>
              {!isExpanded && report.service_entries.length > 0 && (
                <div className="text-xs text-amber-700 mt-1">
                  {report.service_entries.length} service{report.service_entries.length === 1 ? '' : 's'} logged — tap to expand
                </div>
              )}
            </CardHeader>
            {isExpanded && (
              <CardContent className="space-y-4">
                {services.map(service => {
                  const active = activeServices.has(service);
                  const entry = report.service_entries.find(e => e.service === service) || {};
                  const metric = SERVICE_METRICS[service];  // { key, label, placeholder } or undefined
                  return (
                    <div
                      key={service}
                      className={`rounded-md border ${active ? 'border-amber-300 bg-amber-50' : 'border-slate-200'} p-3`}
                    >
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`svc-${job.id}-${service}`}
                          checked={active}
                          onCheckedChange={() => {
                            if (active) removeEntry(job.id, service);
                            else ensureEntry(job.id, service);
                          }}
                          data-testid={`daily-report-service-${service.replace(/\s+/g, '-').toLowerCase()}-${job.id}`}
                        />
                        <Label htmlFor={`svc-${job.id}-${service}`} className="font-semibold cursor-pointer">
                          {service}
                        </Label>
                      </div>

                      {active && (
                        <div className="mt-3 space-y-3">
                          {metric && (
                            <div>
                              <Label className="text-xs text-slate-600">{metric.label}</Label>
                              <Input
                                type="number"
                                inputMode="numeric"
                                min="0"
                                placeholder={metric.placeholder}
                                value={entry[metric.key] ?? ''}
                                onChange={(e) =>
                                  updateEntry(job.id, service, { [metric.key]: e.target.value })
                                }
                                data-testid={`daily-report-metric-${metric.key}-${job.id}`}
                                className="h-11 text-base"
                              />
                            </div>
                          )}

                          <div>
                            <Label className="text-xs text-slate-600">Notes for the office</Label>
                            <Textarea
                              placeholder="e.g. Hit rock at hole 3, delayed 90 min. Used V1 VacCon + TR2 trailer."
                              value={entry.notes || ''}
                              onChange={(e) =>
                                updateEntry(job.id, service, { notes: e.target.value })
                              }
                              rows={3}
                              data-testid={`daily-report-notes-${job.id}-${service.replace(/\s+/g, '-').toLowerCase()}`}
                            />
                          </div>

                          <EquipmentPicker
                            jobId={job.id}
                            service={service}
                            options={equipmentOptions}
                            recent={recentEquipment}
                            selected={entry.equipment_used || []}
                            onToggle={(item) => toggleEquipment(job.id, service, item)}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}

                <Button
                  onClick={() => saveReport(job.id)}
                  disabled={!!saving[job.id]}
                  className="w-full h-12 bg-amber-600 hover:bg-amber-700"
                  data-testid={`daily-report-save-${job.id}`}
                >
                  {saving[job.id] ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" />Save Report</>
                  )}
                </Button>
              </CardContent>
            )}
          </Card>
        );
      })}

      {/* Delete-confirmation dialog. Shared across all cards; opened by
          setPendingDelete. Uses type-nothing confirmation (already an
          admin-only endpoint on the server, and this UI only exposes the
          button when the user can act — but a modal still prevents an
          accidental fat-finger tap on mobile). */}
      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(v) => { if (!v) setPendingDelete(null); }}
      >
        <AlertDialogContent data-testid="daily-report-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Team Lead Report</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the Team Lead Report for{' '}
              <span className="font-semibold text-slate-800">{pendingDelete?.jobTitle}</span>
              {' '}on <span className="font-semibold text-slate-800">{date}</span>, including
              all logged services, hole counts, notes, and equipment.
              <br /><br />
              The job itself, timesheet entries, and equipment records are not affected —
              only this daily report is removed.
              <br /><br />
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="daily-report-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="daily-report-delete-confirm"
            >
              Delete Report
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// -----------------------------------------------------------------------------
// EquipmentPicker: compact multi-select for the assets list. Selected items
// render as removable chips; a "+ Add equipment" button expands a filtered
// picker below. Kept inline so the whole feature lives in one file.
// -----------------------------------------------------------------------------
function EquipmentPicker({ jobId, service, options, recent = [], selected, onToggle }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');

  const selectedIds = new Set((selected || []).map(s => s.id));
  const filtered = filter
    ? options.filter(o =>
        (o.name + ' ' + (o.category || '') + ' ' + (o.code || ''))
          .toLowerCase()
          .includes(filter.toLowerCase())
      )
    : options;

  // "Recent" chips: the lead's top-used equipment from the last 21 days
  // that isn't already on this service entry. One-tap adds it — saves
  // scrolling and filtering through the full picker on every job.
  const recentToShow = (recent || []).filter(r => !selectedIds.has(r.id));

  return (
    <div>
      <Label className="text-xs text-slate-600 block mb-1">Equipment used</Label>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {(selected || []).length === 0 && !open && (
          <span className="text-xs text-slate-400 italic">No equipment logged yet</span>
        )}
        {(selected || []).map(item => (
          <span
            key={item.id}
            className="inline-flex items-center gap-1 text-xs bg-white border border-amber-300 rounded-full px-2 py-1"
            data-testid={`equipment-chip-${item.id}`}
          >
            {item.name}
            <button
              type="button"
              onClick={() => onToggle(item)}
              className="text-slate-400 hover:text-red-500"
              aria-label={`Remove ${item.name}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>

      {/* One-tap "Recent" row — the fast path for common loadouts */}
      {recentToShow.length > 0 && (
        <div className="mb-2">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">
            Recent — tap to add
          </div>
          <div className="flex flex-wrap gap-1.5">
            {recentToShow.map(item => (
              <button
                key={`recent-${item.id}`}
                type="button"
                onClick={() => onToggle(item)}
                className="text-xs bg-slate-50 border border-slate-200 rounded-full px-2 py-1 hover:bg-amber-50 hover:border-amber-300"
                data-testid={`equipment-recent-${item.id}`}
              >
                + {item.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen(o => !o)}
        className="h-8 text-xs"
        data-testid={`equipment-picker-toggle-${jobId}-${service.replace(/\s+/g, '-').toLowerCase()}`}
      >
        <Plus className="w-3 h-3 mr-1" />
        {open ? 'Hide equipment' : 'Add equipment'}
      </Button>
      {open && (
        <div className="mt-2 border rounded-md bg-white max-h-64 overflow-y-auto">
          <div className="p-2 border-b sticky top-0 bg-white">
            <Input
              placeholder="Filter by name, category, or #"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="h-9 text-sm"
              data-testid={`equipment-picker-filter-${jobId}`}
            />
          </div>
          {filtered.length === 0 && (
            <div className="p-3 text-xs text-slate-500 italic">No matching equipment.</div>
          )}
          {filtered.map(item => {
            const isSel = selectedIds.has(item.id);
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onToggle(item)}
                className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50 border-b border-slate-100 ${
                  isSel ? 'bg-amber-50' : ''
                }`}
                data-testid={`equipment-option-${item.id}`}
              >
                <div>
                  <div className="font-medium">{item.name}</div>
                  <div className="text-xs text-slate-500">
                    {item.code}{item.category ? ` · ${item.category}` : ''}
                  </div>
                </div>
                {isSel && <span className="text-amber-600 text-lg">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
